/**
 * @file Selectors relevant for {@see VisualWorkspaceModel}.
 * All selectors use workspace v2 data.
 */

import _, {chain, keyBy, sortBy} from 'lodash';
import {createSelector} from 'reselect';

import {IsExpandedPredicate} from '../../../../helpers/expansionTogglingHelpers';
import {findSelectedFilterForInbox} from '../../../../helpers/modelHelpers';
import {
  canBePinnedToTeammateWorkspace,
  canBePinnedToTeamWorkspace,
  VisualCustomView,
} from '../../../../helpers/models/customViewHelpers';
import {AppParametricSelector, wrapInShallowSelector} from '../../../../helpers/reselectHelpers';
import {
  buildVisualCustomViewsPresorted,
  createFlatVisualCustomView,
  ParentTagOverridesMap,
  SelectableTag,
} from '../../../../helpers/visualCustomViewHelpers';
import {CustomViewTypesEnum} from '../../../../models/api/customViewModel';
import {InboxModel} from '../../../../models/api/inboxModel';
import {
  VisualWorkspaceFolderNestedInbox,
  VisualWorkspaceFolderNestedItem,
  VisualWorkspaceFolderNestedLabel,
  VisualWorkspaceFolderRootLabel,
  VisualWorkspaceInbox,
  VisualWorkspaceLens,
  VisualWorkspaceModel,
  VisualWorkspaceTag,
  VisualWorkspaceTeamData,
  VisualWorkspaceTeammate,
} from '../../../../models/api/visualWorkspaceModel';
import {
  WorkspaceFolderNestedLabelsEnum,
  WorkspaceFolderRootLabelsEnum,
  WorkspaceItemTypesEnum,
} from '../../../../models/api/workspaceModel';
import {InboxesFiltersEnum} from '../../../../models/url/inboxesUrlModel';
import {getHasP1AutoRec} from '../../../featureFlags/featureFlagsSelectors';
import {
  getInboxes,
  getUserOwnedInboxes,
  getUserTeamParticipatingInboxes,
} from '../../inboxes/inboxesSelectors';
import {getFlatLenses, getLenses} from '../../lenses/lensesSelectors';
import {getSelectedFilters} from '../../localPreferences/localPreferencesSelectors';
import {getCustomViews, getVisibleTagsFromPreferences} from '../../preferences/customViewsSelectors';
import {
  getIsInboxVisible,
  getLastAutoRecommendation,
  getLastEditModeHelpTip,
  getShouldAlphabetizeWorkspace,
  getVisibleCustomViewIds,
} from '../../preferences/preferencesSelectors';
import {getTags} from '../../tags/tagsSelectors';
import {
  getLazyTeammatesInCommonTeam,
  makeGetTeammateListWithLazyValidation,
  TeammatesSelector,
} from '../../teammates/lazyTeammatesSelectors';
import {getTeammatesInCommonTeam, getUserNamespace} from '../../teammates/teammatesSelectors';
import {getUserTeams} from '../../teams/teamsSelectors';
import {computeWorkspaceSortOrderMap} from '../sort/workspaceSortOrderMapHelpers';
import {
  buildTagSortItems,
  computeTagAlphabetizedSortValue,
  flattenTagSortItems,
} from '../sort/workspaceTagSortHelpers';
import {
  getPersonalWorkspaceNestedFolders,
  getPersonalWorkspaceRootFolders,
  getPersonalWorkspaceTags,
  getTeamContextFilter,
  getWorkspaceAssignedTeammates,
  getWorkspaceLenses,
  getWorkspaceTeams,
  makeGetWorkspaceDelegationsSorted,
  makeGetWorkspaceTeammatesWithLazyValidation,
} from '../workspaceSelectors';
import {
  computeVisualWorkspaceItemIsRecentlyViewed,
  computeWorkspaceItemIsRecentlyViewed,
  computeWorkspaceTimestampMap,
  isOverFourWeeksAgo,
  TagModelMap,
  VisualWorkspaceItemWithTimestamp,
} from './visualWorkspaceHelpers';

/*
 * Types.
 */

type BooleanSelector<P> = AppParametricSelector<P, boolean>;

type VisualWorkspaceTeammatesSelector<P> = AppParametricSelector<P, ReadonlyArray<VisualWorkspaceTeammate>>;
type VisualWorkspaceTagsSelector<P> = AppParametricSelector<P, ReadonlyArray<VisualWorkspaceTag>>;
type VisualWorkspaceLensesSelector<P> = AppParametricSelector<P, ReadonlyArray<VisualWorkspaceLens>>;

type IsExpandedPredicateSelector<P> = AppParametricSelector<P, IsExpandedPredicate>;

type OptionalTagModelMapSelector<P> = AppParametricSelector<P, TagModelMap | undefined>;
type OptionalParentTagOverridesMapSelector<P> = AppParametricSelector<P, ParentTagOverridesMap | undefined>;

/*
 * Constants.
 */

const requiredNestedFolders = Object.freeze([
  WorkspaceFolderNestedLabelsEnum.ASSIGNED_TO_ME,
  WorkspaceFolderNestedLabelsEnum.SHARED_WITH_ME,
  WorkspaceFolderNestedLabelsEnum.DISCUSSIONS,
]);

const defaultRootFolders = Object.freeze([
  WorkspaceFolderRootLabelsEnum.STARRED,
  WorkspaceFolderRootLabelsEnum.DRAFTS,
  WorkspaceFolderRootLabelsEnum.SENT,
]);

/*
 * Selectors.
 */

/**
 * Represents the top-level folder labels in the personal workspace such as Drafts, Starred, Sent.
 * Does not obey alphabetization preference; these cannot be reordered. Order comes from {@see defaultRootFolders}.
 */
export const getUserVisualWorkspaceRootFolders = createSelector(
  [getPersonalWorkspaceRootFolders],
  (workspaceRootFolders): ReadonlyArray<VisualWorkspaceFolderRootLabel> =>
    _(defaultRootFolders)
      .map((name) => {
        const matchingWorkspaceFolder = workspaceRootFolders.find((folder) => folder.name === name);
        return {
          type: WorkspaceItemTypesEnum.LABEL as const,
          id: name,
          isSelected: matchingWorkspaceFolder ? !matchingWorkspaceFolder.is_hidden : true,
        };
      })
      .value(),
);

export const getUserVisualWorkspaceNestedFolders = createSelector(
  [getPersonalWorkspaceNestedFolders, getUserOwnedInboxes],
  (workspaceNestedFolders, inboxes): ReadonlyArray<VisualWorkspaceFolderNestedItem> => {
    const visualWorkspaceNestedLabels = requiredNestedFolders.map(
      (name): VisualWorkspaceFolderNestedLabel => ({
        type: WorkspaceItemTypesEnum.LABEL as const,
        id: name,
        isSelected: true,
      }),
    );

    const visualWorkspaceNestedInboxes = inboxes.map(
      (inbox): VisualWorkspaceFolderNestedInbox => ({
        type: WorkspaceItemTypesEnum.INBOX as const,
        id: inbox.id,
        isSelected: true,
      }),
    );

    const visualWorkspaceNestedItems = [...visualWorkspaceNestedLabels, ...visualWorkspaceNestedInboxes];
    // Unlike other sections affected by alphabetization, even when the workspace is alphabetized, we still use the workspace ordering. */
    return sortBy(visualWorkspaceNestedItems, (visualWorkspaceItem) => {
      const workspaceIndex = workspaceNestedFolders.findIndex((workspaceFolder) => {
        const workspaceFolderId =
          workspaceFolder.type === WorkspaceItemTypesEnum.INBOX ? workspaceFolder.id : workspaceFolder.name;
        return workspaceFolderId === visualWorkspaceItem.id;
      });
      return workspaceIndex === -1 ? Number.MAX_SAFE_INTEGER : workspaceIndex;
    });
  },
);

export function makeGetUserVisualWorkspaceTeammateWorkspaceTags<P>(getShouldAlphabetize: BooleanSelector<P>) {
  return createSelector(
    [getVisibleTagsFromPreferences, getPersonalWorkspaceTags, getShouldAlphabetize, getHasP1AutoRec],
    (
      visibleTags,
      personalWorkspaceTags,
      shouldAlphabetize,
      hasP1AutoRec,
    ): ReadonlyArray<VisualWorkspaceTag> => {
      const sortItems = buildTagSortItems({
        visibleTags,
        workspaceTags: personalWorkspaceTags,
        shouldAlphabetize,
        hasP1AutoRec,
      });
      const flattenedSortItems = flattenTagSortItems(sortItems);
      return flattenedSortItems.map(({tag, isRecentlyViewed}) => ({
        id: tag.id,
        isSelected: true,
        isRecentlyViewed,
      }));
    },
  );
}

/** Sorted according to teammate alphabetization preference. */
export const getUserVisualWorkspaceTeammateWorkspaceTagsSorted =
  makeGetUserVisualWorkspaceTeammateWorkspaceTags(getShouldAlphabetizeWorkspace);

export function makeGetUserVisualWorkspaceTeammates<P>(
  getShouldAlphabetize: BooleanSelector<P>,
  tag: string,
) {
  return createSelector(
    [
      wrapInShallowSelector(makeGetWorkspaceTeammatesWithLazyValidation<P>(getShouldAlphabetize, tag)),
      getWorkspaceAssignedTeammates,
      getHasP1AutoRec,
    ],
    (teammates, workspaceTeammates, hasP1AutoRec) => {
      const timestampMap = computeWorkspaceTimestampMap(workspaceTeammates);
      return teammates.map((teammate) => ({
        id: teammate.id,
        isSelected: true,
        isRecentlyViewed: computeVisualWorkspaceItemIsRecentlyViewed(teammate.id, timestampMap, hasP1AutoRec),
      }));
    },
  );
}

export function makeGetUserVisualWorkspaceTeams<P>(getShouldAlphabetize: BooleanSelector<P>) {
  return createSelector(
    [
      getUserTeams,
      getUserTeamParticipatingInboxes,
      getWorkspaceTeams,
      getIsInboxVisible,
      getShouldAlphabetize,
      getVisibleCustomViewIds,
      getTags,
      getHasP1AutoRec,
    ],
    (
      userTeams,
      userTeamInboxes,
      workspaceTeams,
      isInboxVisible,
      shouldAlphabetize,
      visibleCustomViewIds,
      tags,
      hasP1AutoRec,
    ): ReadonlyArray<VisualWorkspaceTeamData> =>
      userTeams.map((team): VisualWorkspaceTeamData => {
        const teamWorkspace = workspaceTeams[team.id];

        const inboxSortOrderMap = shouldAlphabetize
          ? {}
          : computeWorkspaceSortOrderMap(teamWorkspace?.inboxes ?? [], ({id}) => id);
        const tagSortOrderMap = shouldAlphabetize
          ? {}
          : computeWorkspaceSortOrderMap(teamWorkspace?.tags ?? [], ({id}) => id);

        const teamInboxes = _(userTeamInboxes)
          .filter((inbox) => inbox.namespace === team.namespace && isInboxVisible(inbox.id))
          // The inboxes already come alphabetized. inboxSortOrderMap is an empty object when shouldAlphabetize=true.
          .sortBy((inbox) => inboxSortOrderMap[inbox.id])
          .value();

        /** Since we don't allow nesting of team custom views, we can bypass calling {@see buildTagSortItems}
         *  and then {@see flattenTagSortItems}--like we do for user workspace tags--to accurately sort them.
         */
        const teamTags = _(visibleCustomViewIds)
          .map((id) => tags[id])
          .compact()
          .filter((tag) => canBePinnedToTeamWorkspace(tag, team.namespace))
          .sortBy((tag) =>
            shouldAlphabetize ? computeTagAlphabetizedSortValue(tag) : tagSortOrderMap[tag.id],
          )
          .value();

        const teamInboxTimestampMap = computeWorkspaceTimestampMap(teamWorkspace?.inboxes ?? []);
        const teamTagTimestampMap = computeWorkspaceTimestampMap(teamWorkspace?.tags ?? []);

        return {
          teamId: team.id,
          inboxes: teamInboxes.map((teamInbox) => ({
            id: teamInbox.id,
            isSelected: true,
            isRecentlyViewed: computeVisualWorkspaceItemIsRecentlyViewed(
              teamInbox.id,
              teamInboxTimestampMap,
              hasP1AutoRec,
            ),
          })),
          customViews: teamTags.map((teamTag) => ({
            id: teamTag.id,
            isSelected: true,
            isRecentlyViewed: computeVisualWorkspaceItemIsRecentlyViewed(
              teamTag.id,
              teamTagTimestampMap,
              hasP1AutoRec,
            ),
          })),
        };
      }),
  );
}

export function makeGetUserVisualWorkspace<P = void | {}>(
  getShouldAlphabetize: BooleanSelector<P>,
  tag: string,
) {
  return createSelector(
    [
      getUserVisualWorkspaceRootFolders,
      getUserVisualWorkspaceNestedFolders,
      makeGetUserVisualWorkspaceTeammateWorkspaceTags(getShouldAlphabetize),
      makeGetUserVisualWorkspaceLenses(getShouldAlphabetize),
      makeGetUserVisualWorkspaceTeams(getShouldAlphabetize),
      makeGetVisualWorkspaceDelegations(getShouldAlphabetize),
      makeGetUserVisualWorkspaceTeammates(getShouldAlphabetize, tag),
      getUserNamespace,
      getShouldAlphabetize,
    ],
    (
      visualWorkspaceFolderLabels,
      visualWorkspaceNestedFolders,
      visualWorkspaceTeammateWorkspaceTags,
      visualWorkspaceLenses,
      visualWorkspaceTeams,
      visualWorkspaceDelegations,
      visualWorkspaceTeammates,
      userNamespace,
      shouldAlphabetize,
    ): VisualWorkspaceModel => ({
      isAlphabetized: shouldAlphabetize,
      teammateNamespace: userNamespace,
      nestedFolders: visualWorkspaceNestedFolders,
      rootFolders: visualWorkspaceFolderLabels,
      teammateWorkspaceTags: visualWorkspaceTeammateWorkspaceTags,
      lenses: visualWorkspaceLenses,
      teams: visualWorkspaceTeams,
      delegations: visualWorkspaceDelegations,
      teammates: visualWorkspaceTeammates,
    }),
  );
}

/** Sorted according to teammate alphabetization preference. */
export const getUserVisualWorkspace = makeGetUserVisualWorkspace(
  getShouldAlphabetizeWorkspace,
  'getUserVisualWorkspace',
);

export function makeGetVisualWorkspaceVisibleInboxesForTeam<P>(
  getVisualWorkspaceInboxesForTeam: AppParametricSelector<P, ReadonlyArray<VisualWorkspaceInbox>>,
) {
  return createSelector(
    [getVisualWorkspaceInboxesForTeam, getInboxes],
    (visualWorkspaceInboxesForTeam, inboxes): ReadonlyArray<InboxModel> =>
      chain(visualWorkspaceInboxesForTeam)
        .filter(({isSelected}) => isSelected)
        .map(({id}) => inboxes[id])
        .compact()
        .value(),
  );
}

export function makeGetTeamInboxSelectedFilter<P>(
  getInboxId: AppParametricSelector<P, number>,
): AppParametricSelector<P, InboxesFiltersEnum> {
  return createSelector(
    [getSelectedFilters, getTeamContextFilter, getInboxId],
    (selectedFilters, teamContextFilter, inboxId) =>
      teamContextFilter || findSelectedFilterForInbox(selectedFilters, inboxId),
  );
}

function makeGetVisibleTeammatesForVisualWorkspaceTeammates<P>(
  getVisualWorkspaceTeammatesList: VisualWorkspaceTeammatesSelector<P>,
  getTeammatesList: TeammatesSelector<P>,
): TeammatesSelector<P> {
  return createSelector(
    [getVisualWorkspaceTeammatesList, getTeammatesList],
    (visualWorkspaceTeammates, teammatesList) => {
      const teammatesById = keyBy(teammatesList, 'id');
      return chain(visualWorkspaceTeammates)
        .filter(({isSelected}) => isSelected)
        .map(({id}) => teammatesById[id])
        .compact()
        .value();
    },
  );
}

export function makeGetVisualWorkspaceVisualCustomViewsForTeam<P>(
  getVisualWorkspaceCustomViewsForTeam: VisualWorkspaceTagsSelector<P>,
  getOptionalAdditionalTagsMap: OptionalTagModelMapSelector<P>,
  shouldRestrictToVisibleItems: boolean,
) {
  return createSelector(
    [getVisualWorkspaceCustomViewsForTeam, getCustomViews, getTags, getOptionalAdditionalTagsMap],
    (visualWorkspaceCustomViewsForTeam, customViews, tagsFromState, optionalAdditionalTagsMap) => {
      const additionalTagsMap = optionalAdditionalTagsMap ?? {};
      return chain(visualWorkspaceCustomViewsForTeam)
        .filter(({isSelected}) => (shouldRestrictToVisibleItems ? isSelected : true))
        .map((visualWorkspaceCustomView): VisualCustomView | null => {
          // Currently, the additional tags are supplied during edit mode for tags being newly pinned to a workspace.
          //  Because additional custom views aren't also supplied, this may result in an undefined visualCustomView.uid.
          //  That is OK because the uid isn't used inside edit mode.
          const tagId = visualWorkspaceCustomView.id;
          const tag = tagsFromState[tagId] ?? additionalTagsMap[tagId];
          const customView = customViews[tagId];

          return tag
            ? createFlatVisualCustomView({
                tag,
                customView,
                type: CustomViewTypesEnum.TAG,
                isVisibleInWorkspace: visualWorkspaceCustomView.isSelected,
              })
            : null;
        })
        .compact()
        .value();
    },
  );
}

interface MakeGetTeammateWorkspacePresortedSelectableTagsParams<P> {
  /**
   * Selects a representation of both private and team tags that appear in this workspace.
   * It is assumed these have been presorted, so no further sorting is necessary.
   */
  getVisualWorkspaceTags: VisualWorkspaceTagsSelector<P>;
  getOptionalAdditionalTagsMap: OptionalTagModelMapSelector<P>;
  shouldRestrictToVisibleItems: boolean;
}

interface GetVisualWorkspaceTeammateWorkspaceVisualCustomViewsParams<P>
  extends MakeGetTeammateWorkspacePresortedSelectableTagsParams<P> {
  getOptionalParentTagOverridesMap: OptionalParentTagOverridesMapSelector<P>;
  getFindIsItemExpanded: IsExpandedPredicateSelector<P>;
  defaultExpandedState: boolean;
}

function makeGetTeammateWorkspacePresortedSelectableTags<P>(
  params: MakeGetTeammateWorkspacePresortedSelectableTagsParams<P>,
) {
  return createSelector(
    [params.getVisualWorkspaceTags, getTags, params.getOptionalAdditionalTagsMap],
    (visualWorkspaceTags, tagsFromState, additionalTagsMap = {}): ReadonlyArray<SelectableTag> =>
      _(visualWorkspaceTags)
        .filter(({isSelected}) => (params.shouldRestrictToVisibleItems ? isSelected : true))
        .map(({id, isSelected}) => {
          const tag = tagsFromState[id] ?? additionalTagsMap[id];
          return tag ? {tag, isSelected} : null;
        })
        .compact()
        .filter(({tag}) => canBePinnedToTeammateWorkspace(tag))
        .value(),
  );
}

export function makeGetVisualWorkspaceTeammateWorkspaceVisualCustomViews<P>(
  params: GetVisualWorkspaceTeammateWorkspaceVisualCustomViewsParams<P>,
) {
  return createSelector(
    [
      makeGetTeammateWorkspacePresortedSelectableTags({
        getVisualWorkspaceTags: params.getVisualWorkspaceTags,
        getOptionalAdditionalTagsMap: params.getOptionalAdditionalTagsMap,
        shouldRestrictToVisibleItems: params.shouldRestrictToVisibleItems,
      }),
      getCustomViews,
      params.getOptionalParentTagOverridesMap,
      params.getFindIsItemExpanded,
    ],
    (presortedSelectableTags, customViews, optionalParentTagOverridesMap, findIsItemExpanded) =>
      buildVisualCustomViewsPresorted({
        presortedSelectableTags,
        customViews,
        parentTagOverridesMap: optionalParentTagOverridesMap,
        findIsExpanded: findIsItemExpanded,
        defaultExpandedState: params.defaultExpandedState,
      }),
  );
}

export function makeGetUserVisualWorkspaceLenses<P>(getShouldAlphabetize: BooleanSelector<P>) {
  return createSelector(
    [getFlatLenses, getWorkspaceLenses, getShouldAlphabetize, getHasP1AutoRec],
    (alphabeticallySortedLenses, workspaceLenses, shouldAlphabetize, hasP1AutoRec) => {
      const sortOrderMap = shouldAlphabetize
        ? computeWorkspaceSortOrderMap(alphabeticallySortedLenses, ({id}) => id)
        : {};

      return chain(workspaceLenses)
        .sortBy(({id}) => sortOrderMap[id])
        .map((workspaceLens) => ({
          id: workspaceLens.id,
          isSelected: true,
          isRecentlyViewed: computeWorkspaceItemIsRecentlyViewed(workspaceLens.last_viewed, hasP1AutoRec),
        }))
        .value();
    },
  );
}

export function makeGetVisualWorkspaceLenses<P>(getVisualWorkspaceLenses: VisualWorkspaceLensesSelector<P>) {
  return createSelector([getVisualWorkspaceLenses, getLenses], (visualWorkspaceLenses, lenses) =>
    chain(visualWorkspaceLenses)
      .filter(({isSelected}) => isSelected)
      .map(({id}) => lenses[id])
      .compact()
      .value(),
  );
}

export function makeGetVisualWorkspaceDelegations<P>(getShouldAlphabetize: BooleanSelector<P>) {
  return createSelector([makeGetWorkspaceDelegationsSorted(getShouldAlphabetize)], (sortedDelegations) =>
    sortedDelegations.map(({id}) => ({id, isSelected: true})),
  );
}

export function makeGetVisualWorkspaceVisibleTeammates<P>(
  getVisualWorkspaceTeammatesList: VisualWorkspaceTeammatesSelector<P>,
) {
  return makeGetTeammateListWithLazyValidation<P>(
    (getTeammates) =>
      makeGetVisibleTeammatesForVisualWorkspaceTeammates(getVisualWorkspaceTeammatesList, getTeammates),
    getTeammatesInCommonTeam,
    getLazyTeammatesInCommonTeam,
    'makeGetVisualWorkspaceVisibleTeammates',
  );
}

// We may recommend decluttering if there exists some item that isn't recently viewed.
function computeShouldDeclutter(workspaceItems: ReadonlyArray<VisualWorkspaceItemWithTimestamp>): boolean {
  // In slow networks, upon saving the workspace, some items may have undefined values, so we explicity check for false.
  return workspaceItems.some((item) => item.isRecentlyViewed === false);
}

export const getShouldRecommendDeclutter = createSelector([getUserVisualWorkspace], (visualWorkspace) => {
  // Check whether there are workspace items with timestamps that haven't been viewed for over 4 weeks.
  const shouldDeclutterTeams = visualWorkspace.teams.some(
    (teamData) => computeShouldDeclutter(teamData.inboxes) || computeShouldDeclutter(teamData.customViews),
  );

  return (
    shouldDeclutterTeams ||
    computeShouldDeclutter(visualWorkspace.teammateWorkspaceTags) ||
    computeShouldDeclutter(visualWorkspace.teammates)
  );
});

export const getShouldShowP1AutoRecBanner = createSelector(
  [getShouldRecommendDeclutter, getLastAutoRecommendation],
  (shouldRecommendDeclutter, lastAutoRecommendation) => {
    // Make sure the user doesn't see the banner if they've already interacted with it within 4 weeks.
    if (isOverFourWeeksAgo(lastAutoRecommendation)) {
      return shouldRecommendDeclutter;
    }

    return false;
  },
);

export const getShouldShowEditModeHelpTip = createSelector(
  [getShouldRecommendDeclutter, getLastEditModeHelpTip, getLastAutoRecommendation],
  (shouldRecommendDeclutter, lastEditModeHelpTip, lastAutoRecommendation) => {
    // Checks if the help tip was seen over four weeks ago OR
    // if help tip timestamp hasn't been initialized and last auto rec is over four weeks ago.
    if (
      isOverFourWeeksAgo(lastEditModeHelpTip) ||
      (lastEditModeHelpTip === undefined && isOverFourWeeksAgo(lastAutoRecommendation))
    ) {
      return shouldRecommendDeclutter;
    }
    return false;
  },
);

// Computes the number of rarely viewed items in the workspace. Used in P1 edit mode auto recommendations help tip.
export const getVisualWorkspaceRarelyViewedCount = createSelector(
  [getUserVisualWorkspace],
  (visualWorkspace) => {
    const rarelyViewedTagCount = visualWorkspace.teammateWorkspaceTags.filter(
      (tag) => tag.isRecentlyViewed === false,
    ).length;

    const rarelyViewedTeamCount = visualWorkspace.teams.reduce(
      (result, teamData) =>
        result +
        teamData.customViews.filter((customView) => customView.isRecentlyViewed === false).length +
        teamData.inboxes.filter((inbox) => inbox.isRecentlyViewed === false).length,
      0,
    );

    const rarelyViewedTeammatesCount = visualWorkspace.teammates.filter(
      (teammate) => teammate.isRecentlyViewed === false,
    ).length;

    return rarelyViewedTagCount + rarelyViewedTeamCount + rarelyViewedTeammatesCount;
  },
);
